import React, { useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Box
} from "@material-ui/core";
import {
  DeleteOutlined as DeleteIcon,
  Save as SaveIcon
} from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  listAllRemyndBrands,
  listAllRemyndCatLv1s,
  listAllRemyndCatLv2s,
  listAllRemyndCatLv3s,
  listAllPartners,
  listAllPartnerProducts
} from "src/apps/commons/dropdowns/api";
import AsyncDropdown from "src/apps/commons/dropdowns/components/AsyncDropdown";
import { toDropdown } from "src/utils";
import { SubHeader } from "../../../commons/dataDisplays";
import { ImageUpload } from "../../../commons/fileUploads";
import {
  IRemyndProduct,
  IRemyndProductSaveReq,
  IRemyndProductSaveForm
} from "../types";
import { paths } from "../utils";
import { yupParseNumber, yupTrimToNull } from "src/utils/stringUtils";

const useStyles = makeStyles({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  }
});

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  data?: IRemyndProduct;
  onSubmit: (req: IRemyndProductSaveReq) => void;
  onDelete?: () => void;
}

const schema = yup.object().shape({
  code: yup.string().trim(),
  nameTh: yup.string().trim().required("กรุณาระบุ ชื่อ (TH)"),
  nameEn: yup.string().trim().required("กรุณาระบุ ชื่อ (EN)"),
  highlighted: yup.boolean(),
  highlightPriority: yup
    .mixed()
    .transform(yupParseNumber)
    .test(
      "require-highlightPriority-if-highlighted",
      "กรุณาระบุ Priority",
      (value, context) => (context.parent.highlighted ? value != null : true)
    ),
  searchable: yup.boolean(),
  canceled: yup.boolean(),
  active: yup.boolean(),
  refProductCode: yup.string().transform(yupTrimToNull),
  barcode: yup.string().transform(yupTrimToNull),
  categoryTextEn: yup.string().transform(yupTrimToNull),
  categoryTextTh: yup.string().transform(yupTrimToNull),
  seriesEn: yup.string().transform(yupTrimToNull),
  seriesTh: yup.string().transform(yupTrimToNull),
  descEn: yup.string().transform(yupTrimToNull),
  descTh: yup.string().transform(yupTrimToNull),
  desc2En: yup.string().transform(yupTrimToNull),
  desc2Th: yup.string().transform(yupTrimToNull),
  desc3En: yup.string().transform(yupTrimToNull),
  desc3Th: yup.string().transform(yupTrimToNull),
  cautionEn: yup.string().transform(yupTrimToNull),
  cautionTh: yup.string().transform(yupTrimToNull),
  imageId: yup.string().transform(yupTrimToNull)
});

const Form: React.FC<Props> = props => {
  const { isLoading, isSaving, data, onSubmit, onDelete } = props;
  const classes = useStyles();
  const router = useRouter();

  const defaultFormData = useMemo(
    () => ({
      code: data?.code ?? "",
      nameTh: data?.nameTh ?? "",
      nameEn: data?.nameEn ?? "",
      highlighted: data?.highlightPriority != null,
      highlightPriority: data?.highlightPriority ?? 1,
      searchable: data?.searchable ?? false,
      canceled: data?.canceled ?? false,
      active: data?.active ?? true,
      brandDropdown: data?.brand ? toDropdown(data.brand) : null,
      categoryLv1Dropdown: data?.categoryLv1
        ? toDropdown(data.categoryLv1)
        : null,
      categoryLv2Dropdown: data?.categoryLv2
        ? toDropdown(data.categoryLv2)
        : null,
      categoryLv3Dropdown: data?.categoryLv3
        ? toDropdown(data.categoryLv3)
        : null,
      partnerDropdown: data?.partner ? toDropdown(data.partner) : null,
      partnerProductDropdown: data?.partnerProduct
        ? toDropdown(data.partnerProduct)
        : null,
      refProductCode: data?.refProductCode ?? "",
      barcode: data?.barcode ?? "",
      categoryTextEn: data?.categoryTextEn ?? "",
      categoryTextTh: data?.categoryTextTh ?? "",
      seriesEn: data?.seriesEn ?? "",
      seriesTh: data?.seriesTh ?? "",
      descEn: data?.descEn ?? "",
      descTh: data?.descTh ?? "",
      desc2En: data?.desc2En ?? "",
      desc2Th: data?.desc2Th ?? "",
      desc3En: data?.desc3En ?? "",
      desc3Th: data?.desc3Th ?? "",
      cautionEn: data?.cautionEn ?? "",
      cautionTh: data?.cautionTh ?? "",
      imageId: data?.imageId ?? ""
    }),
    [data]
  );
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
    watch
  } = useForm<PartialNull<IRemyndProductSaveForm>>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handleTransformAndSubmit = (req: IRemyndProductSaveForm) => {
    const {
      brandDropdown,
      categoryLv1Dropdown: catLv1DDL,
      categoryLv2Dropdown: catLv2DDL,
      categoryLv3Dropdown: catLv3DDL,
      partnerDropdown: partnerDDL,
      partnerProductDropdown: partnerProductDDL,
      highlightPriority,
      highlighted,
      ...restReq
    } = req;
    onSubmit({
      brandId: brandDropdown?.value,
      categoryLv1Id: catLv1DDL?.value,
      categoryLv2Id: catLv2DDL?.value,
      categoryLv3Id: catLv3DDL?.value,
      partnerId: partnerDDL?.value,
      partnerProductId: partnerProductDDL?.value,
      highlightPriority: highlighted ? highlightPriority ?? 1 : undefined,
      ...restReq
    });
  };

  useEffect(() => {
    if (data) {
      reset(defaultFormData);
    }
  }, [reset, data, defaultFormData]);

  const highlighted = watch("highlighted");
  const categoryLv1Dropdown = watch("categoryLv1Dropdown");
  const categoryLv2Dropdown = watch("categoryLv2Dropdown");
  const partnerDropdown = watch("partnerDropdown");
  const partnerProductDropdown = watch("partnerProductDropdown");
  const hasPartnerProductDropdown = partnerProductDropdown != null;

  return (
    <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
      <Grid container alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="code"
              control={control}
              defaultValue={defaultFormData.code}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  {...field}
                  label="รหัส *"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6} />

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="nameTh"
              control={control}
              defaultValue={defaultFormData.nameTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.nameTh}
                  helperText={errors.nameTh?.message}
                  {...field}
                  label="ชื่อ (TH) *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="nameEn"
              control={control}
              defaultValue={defaultFormData.nameEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.nameEn}
                  helperText={errors.nameEn?.message}
                  {...field}
                  label="ชื่อ (EN) *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="imageId"
            control={control}
            defaultValue={defaultFormData.imageId ?? ""}
            render={({ field: { value, onChange } }) => (
              <ImageUpload
                fileId={value}
                label={"รูปสินค้า"}
                onChange={onChange}
                disabled={isSaving || hasPartnerProductDropdown}
              />
            )}
          />
        </Grid>

        <Grid item>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={defaultFormData.active}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          color="primary"
                          checked={Boolean(value)}
                          onChange={onChange}
                          disabled={isSaving || hasPartnerProductDropdown}
                        />
                      </Box>
                    )}
                  />
                }
                label="Active"
              />
            </FormGroup>
          )}
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="searchable"
                    control={control}
                    defaultValue={defaultFormData.searchable}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          color="primary"
                          checked={Boolean(value)}
                          onChange={onChange}
                          disabled={isSaving || hasPartnerProductDropdown}
                        />
                      </Box>
                    )}
                  />
                }
                label="Searchable"
              />
            </FormGroup>
          )}
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="highlighted"
                    control={control}
                    defaultValue={defaultFormData.highlighted}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          color="primary"
                          checked={Boolean(value)}
                          onChange={onChange}
                          disabled={isSaving || hasPartnerProductDropdown}
                        />
                      </Box>
                    )}
                  />
                }
                label="Highlighted"
              />
              {Boolean(highlighted) && (
                <Box ml={2}>
                  <Controller
                    name="highlightPriority"
                    control={control}
                    defaultValue={defaultFormData.highlightPriority}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        disabled={isSaving || hasPartnerProductDropdown}
                        error={!!errors.highlightPriority}
                        helperText={errors.highlightPriority?.message}
                        {...field}
                        label="Priority *"
                      />
                    )}
                  />
                </Box>
              )}
            </FormGroup>
          )}
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="canceled"
                    control={control}
                    defaultValue={defaultFormData.canceled}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          color="primary"
                          checked={Boolean(value)}
                          onChange={onChange}
                          disabled={isSaving || hasPartnerProductDropdown}
                        />
                      </Box>
                    )}
                  />
                }
                label="Canceled"
              />
            </FormGroup>
          )}
        </Grid>

        <Grid item xs={12}>
          <SubHeader title="หมวดหมู่" marginTop />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="categoryLv1Dropdown"
              control={control}
              defaultValue={defaultFormData.categoryLv1Dropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled={isSaving || hasPartnerProductDropdown}
                  label={"หมวดสินค้า Level 1"}
                  fetchDropdown={listAllRemyndCatLv1s}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  onChange={v => {
                    onChange(v);
                    setValue("categoryLv2Dropdown", null);
                    setValue("categoryLv3Dropdown", null);
                  }}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="categoryLv2Dropdown"
              control={control}
              defaultValue={defaultFormData.categoryLv2Dropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled={
                    isSaving ||
                    hasPartnerProductDropdown ||
                    !categoryLv1Dropdown
                  }
                  label={
                    categoryLv1Dropdown
                      ? "หมวดสินค้า Level 2"
                      : "กรุณาเลือก หมวดสินค้า Level 1"
                  }
                  fetchDropdown={listAllRemyndCatLv2s}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  additionalReqParams={{
                    search: { lv1Id: categoryLv1Dropdown?.value }
                  }}
                  onChange={v => {
                    onChange(v);
                    setValue("categoryLv3Dropdown", null);
                  }}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="categoryLv3Dropdown"
              control={control}
              defaultValue={defaultFormData.categoryLv3Dropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled={
                    isSaving ||
                    hasPartnerProductDropdown ||
                    !categoryLv2Dropdown
                  }
                  label={
                    categoryLv2Dropdown
                      ? "หมวดสินค้า Level 3"
                      : "กรุณาเลือก หมวดสินค้า Level 2"
                  }
                  fetchDropdown={listAllRemyndCatLv3s}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  additionalReqParams={{
                    search: { lv2Id: categoryLv2Dropdown?.value }
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} />

        <Grid item xs={12}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="categoryTextTh"
              control={control}
              defaultValue={defaultFormData.categoryTextTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.categoryTextTh}
                  helperText={errors.categoryTextTh?.message}
                  {...field}
                  label="หมวดสินค้า (text) (TH)"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="categoryTextEn"
              control={control}
              defaultValue={defaultFormData.categoryTextEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.categoryTextEn}
                  helperText={
                    errors.categoryTextEn?.message ??
                    "คั่นแต่ละ level ด้วยสัญลักษณ์ > (ใส่ space ครอบได้) และภาษาไทย-อังกฤษ ต้องมีจำนวน level เท่ากัน"
                  }
                  {...field}
                  label="หมวดสินค้า (text) (EN)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <SubHeader title="แบรนด์" marginTop />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="brandDropdown"
              control={control}
              defaultValue={defaultFormData.brandDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled={isSaving || hasPartnerProductDropdown}
                  label="แบรนด์"
                  fetchDropdown={listAllRemyndBrands}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <SubHeader title="Remynd Partner" marginTop />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="partnerDropdown"
              control={control}
              defaultValue={defaultFormData.partnerDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled={isSaving || hasPartnerProductDropdown}
                  label={"Partner"}
                  fetchDropdown={listAllPartners}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME",
                      desc: false
                    }
                  }}
                  additionalReqParams={{ excludeRemynd: true }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="partnerProductDropdown"
              control={control}
              defaultValue={defaultFormData.partnerProductDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  disabled={isSaving}
                  label={"Partner Product"}
                  fetchDropdown={listAllPartnerProducts}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  additionalReqParams={{
                    partnerId: partnerDropdown?.meta.id
                  }}
                  onChange={v => {
                    onChange(v);
                    setValue(
                      "partnerDropdown",
                      v?.meta.partner ? toDropdown(v?.meta.partner) : null
                    );
                  }}
                  value={value}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <SubHeader title="รายละเอียดเพิ่มเติม" marginTop />
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="refProductCode"
              control={control}
              defaultValue={defaultFormData.refProductCode}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.refProductCode}
                  helperText={errors.refProductCode?.message}
                  {...field}
                  label="Ref Product Code"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="barcode"
              control={control}
              defaultValue={defaultFormData.barcode}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.barcode}
                  helperText={errors.barcode?.message}
                  {...field}
                  label="Barcode"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="seriesTh"
              control={control}
              defaultValue={defaultFormData.seriesTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.seriesTh}
                  helperText={errors.seriesTh?.message}
                  {...field}
                  label="Series (TH)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="seriesEn"
              control={control}
              defaultValue={defaultFormData.seriesEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.seriesEn}
                  helperText={errors.seriesEn?.message}
                  {...field}
                  label=" Series (EN)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="descTh"
              control={control}
              defaultValue={defaultFormData.descTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.descTh}
                  helperText={errors.descTh?.message}
                  {...field}
                  label="รายละเอียด (TH)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="descEn"
              control={control}
              defaultValue={defaultFormData.descEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.descEn}
                  helperText={errors.descEn?.message}
                  {...field}
                  label="รายละเอียด (EN)"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="desc2Th"
              control={control}
              defaultValue={defaultFormData.desc2Th}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.desc2Th}
                  helperText={errors.desc2Th?.message}
                  {...field}
                  label="รายละเอียด 2 (TH)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="desc2En"
              control={control}
              defaultValue={defaultFormData.desc2En}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.desc2En}
                  helperText={errors.desc2En?.message}
                  {...field}
                  label="รายละเอียด 2 (EN)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="desc3Th"
              control={control}
              defaultValue={defaultFormData.desc3Th}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.desc3Th}
                  helperText={errors.desc3Th?.message}
                  {...field}
                  label="รายละเอียด 3 (TH)"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="desc3En"
              control={control}
              defaultValue={defaultFormData.desc3En}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.desc3En}
                  helperText={errors.desc3En?.message}
                  {...field}
                  label="รายละเอียด 3 (EN)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="cautionTh"
              control={control}
              defaultValue={defaultFormData.cautionTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.cautionTh}
                  helperText={errors.cautionTh?.message}
                  {...field}
                  label="ข้อควรระวัง (TH)"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="cautionEn"
              control={control}
              defaultValue={defaultFormData.cautionEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={2}
                  rowsMax={8}
                  disabled={isSaving || hasPartnerProductDropdown}
                  error={!!errors.cautionEn}
                  helperText={errors.cautionEn?.message}
                  {...field}
                  label="ข้อควรระวัง (EN)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Box mt={2} mb={1}>
            <Divider light />
          </Box>
        </Grid>

        <Grid item className={classes.layoutButton} />

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            onClick={() => router.push(paths.index)}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
        </Grid>

        {onDelete && (
          <Grid item className={classes.button}>
            <Button
              variant="contained"
              onClick={onDelete}
              disabled={hasPartnerProductDropdown || isSaving || isLoading}
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </Grid>
        )}

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            color="primary"
            disabled={isSaving || isLoading}
            type="submit"
            startIcon={
              isSaving ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default Form;
